import {Color} from '@benchling/nucleus-ui';
import {FixedSizeList} from 'react-window';

import {assert} from 'Asserts';
import {HitSequence} from 'search/blast/alignmentTypes';
import {
  TABLE_HEADER_HEIGHT,
  TABLE_ROW_BODY_HEIGHT,
  VERTICAL_SCROLLBAR_WIDTH,
} from 'ui-library/scrollable/ScrollableTableConstants';
import {getRowHeightBuffer} from 'ui-library/scrollable/ScrollableTableUtil';
import * as logging from 'util/Logging';

enum DiffType {
  Addition = 'a',
  Deletion = 'd',
  Mismatch = 'm',
}

/**
 * Return the base at index in the hit sequence. The hit sequence is constructed based on the query
 * sequence and diffs.
 *
 * When the sequence exists, the hitSequence#start serves as the starting index of the hit sequence.
 *
 * When the diffs array exists, the hitSequence#start serves as the offset to adjust the start index of
 * the diffs array. This is necessary because the diffs are generated based on the diff region alone, and
 * the indices in the diffs array are from 0 to diff region length. However, the diff region itself may
 * not start from the beginning of the query sequence.
 */
export function getHitBaseAtIndex(querySequence: string, hitSequence: HitSequence, index: number): string {
  const {start, end, sequence, diffs} = hitSequence;
  if (index < start || index > end) {
    return ' ';
  }

  if (sequence != null) {
    return sequence[index - start];
  }
  assert(diffs != null, 'Sequence and diffs should not be null at the same time');

  let low = 0;
  let high = diffs.length - 1;

  // find the relevant diff using binary search
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const diffString = diffs[mid];
    const typeIndex = diffString.search(/[adm]/);
    const type = diffString.slice(typeIndex, typeIndex + 1) as DiffType;
    const diffStartIndex = parseInt(diffString.slice(0, typeIndex), 10) + start;
    const param = diffString.slice(typeIndex + 1);

    const diffEndIndex =
      type === DiffType.Deletion ? diffStartIndex + parseInt(param, 10) : diffStartIndex + param.length;

    if (diffStartIndex > index) {
      high = mid - 1;
    } else if (diffEndIndex <= index) {
      low = mid + 1;
    } else {
      switch (type) {
        case DiffType.Addition:
          return param[index - diffStartIndex];
        case DiffType.Deletion:
          return '-';
        case DiffType.Mismatch:
          return param[index - diffStartIndex];
        default:
          throw new Error(`Invalid diff type: ${type}`);
      }
    }
  }

  return querySequence[index];
}

export function divScrollTo(ref: React.RefObject<HTMLDivElement>, scrollLeft: number): void {
  if (ref.current) {
    if (ref.current.scrollLeft !== scrollLeft) {
      ref.current.scrollTo({left: scrollLeft});
    }
  }
}

export function listScrollTo(ref: React.RefObject<FixedSizeList>, scrollLeft: number): void {
  if (ref.current) {
    ref.current.scrollTo(scrollLeft);
  }
}

export function getAlignmentScoreColor(score: number): string {
  if (score < 40) {
    return Color.TEAL_200;
  } else if (score < 50) {
    return Color.TEAL_300;
  } else if (score < 80) {
    return Color.TEAL_400;
  } else if (score < 200) {
    return Color.TEAL_500;
  } else {
    return Color.BLUE_600;
  }
}

export function getTotalComponentSize(zoom: number, totalWindowLength: number, queryLength: number): number {
  if (zoom < PAIRWISE_ALIGNMENT_ZOOM_THRESHOLD) {
    return totalWindowLength * zoomToGraphScale(zoom);
  } else {
    return zoomToPairwiseItemSize(zoom) * queryLength;
  }
}

const SEQUENCE_COLUMN_LEFT_PADDING = 14;
const SEQUENCE_COLUMN_RIGHT_PADDING = 24;
export const SEQUENCE_COLUMN_PADDING = SEQUENCE_COLUMN_LEFT_PADDING + SEQUENCE_COLUMN_RIGHT_PADDING;

export function insideAlignmentColumn(
  adjustedX: number,
  adjustedY: number,
  isCompact: boolean,
  height: number
): boolean {
  return (
    adjustedX >= -SEQUENCE_COLUMN_LEFT_PADDING &&
    adjustedY >= getQueryRowHeight(isCompact) &&
    adjustedY < height - TABLE_HEADER_HEIGHT - 15
  );
}

// This is the buffer for the vertical scrollbar in the alignment table.
// The scrollbar itself is 15px width. The extra 2px probably comes from borderline.
export const ALIGNMENT_TABLE_SCROLLBAR_BUFFER = VERTICAL_SCROLLBAR_WIDTH + 2;

// 0 is the start pixel of the sequence:
// [-SEQUENCE_COLUMN_LEFT_PADDING, 0) is region in the left padding.
// [0, sequence column width without padding] is the region of the sequence column.
export function getAdjustedX(pageX: number, windowWidth: number, sequenceColumnWidth: number): number {
  const sequenceColumnStart = windowWidth - sequenceColumnWidth - VERTICAL_SCROLLBAR_WIDTH;
  return pageX - sequenceColumnStart - SEQUENCE_COLUMN_LEFT_PADDING;
}

// start from 0 right underneath the table header
export function getAdjustedY(pageY: number, windowHeight: number, alignmentViewHeight: number): number {
  return pageY - (windowHeight - alignmentViewHeight + TABLE_HEADER_HEIGHT);
}

export const PAIRWISE_ALIGNMENT_ZOOM_THRESHOLD = 80;
export const ZOOM_MAX = 150;
export const ZOOM_STEP = 10;

function zoomToGraphScale(zoom: number): number {
  switch (zoom) {
    case 0:
      return 1;
    case 10:
      return 1.3;
    case 20:
      return 1.6;
    case 30:
      return 1.9;
    case 40:
      return 2.2;
    case 50:
      return 2.5;
    case 60:
      return 2.8;
    case 70:
      return 3.1;
    default:
      logging.warn('invalid zoom level');
      return 0;
  }
}

export function zoomToPairwiseItemSize(zoom: number): number {
  switch (zoom) {
    case 80:
      return 12;
    case 90:
      return 15;
    case 100:
      return 18;
    case 110:
      return 21;
    case 120:
      return 24;
    case 130:
      return 27;
    case 140:
      return 30;
    case 150:
      return 35;
    default:
      logging.warn('invalid zoom level');
      return 0;
  }
}

export function roundToNearestMultiple(value: number, multiple: number): number {
  return Math.round(value / multiple) * multiple;
}

export function boundMinMax(min: number, value: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}

export const QUERY_ROW_CONTENT_HEIGHT = 40;

export function getQueryRowHeight(isCompact: boolean): number {
  return QUERY_ROW_CONTENT_HEIGHT + getRowHeightBuffer(isCompact);
}

export function getNormalRowHeight(isCompact: boolean): number {
  return TABLE_ROW_BODY_HEIGHT + getRowHeightBuffer(isCompact);
}

export const SELECTED_ROW = '#fac359';
export const SELECTED_OTHER_ROWS = '#e6e7e9';
